import React from "react";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  type FleetActions,
  type FleetActionCard,
  BUCKET_COLOR,
  BUCKET_HELP,
  stuckLabel,
} from "./fleetActionsFormat";

interface FleetActionsModalProps {
  actions: FleetActions | null;
  bucket: string | null;
  onClose: () => void;
  onSelectBucket: (bucket: string) => void;
}

const PropertyRow: React.FC<{ card: FleetActionCard }> = ({ card }) => (
  <Box sx={{ py: 1.5 }}>
    <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
      <Typography variant="subtitle2" fontWeight={700}>
        {card.name || card.property_id}
      </Typography>
      <Chip size="small" label={card.code.replace(/_/g, " ")} variant="outlined" />
      <Typography variant="caption" color="text.secondary">
        {stuckLabel(card.since)}
        {card.last_scraped ? ` · last seen ${card.last_scraped.slice(0, 10)}` : ""}
      </Typography>
    </Box>

    <Typography variant="body2" sx={{ mt: 0.5 }}>
      {card.guidance}
    </Typography>

    {/* The engine's OWN words, with where they were found. Provenance is not decoration:
        printing the path is what makes a wrong read visible instead of silent. */}
    {card.engine_signal && (
      <Tooltip title={card.engine_signal.path || "path unknown"} placement="top-start">
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          engine signal: “{card.engine_signal.matched}”
        </Typography>
      </Tooltip>
    )}

    {card.threshold_failures.length > 0 && (
      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
        below threshold:{" "}
        {card.threshold_failures
          .map((f) => `${f.metric} ${f.value} < ${f.floor}`)
          .join(" · ")}
      </Typography>
    )}

    {card.playbook && !card.playbook.carried && card.playbook.recommended && (
      <Typography variant="caption" display="block" color="warning.main" sx={{ mt: 0.5 }}>
        repair computed but not applied — recommended “{card.playbook.recommended}”, running “
        {card.playbook.applied || "none"}”
      </Typography>
    )}

    {/* PROOF-0493: two stored class fields can DISAGREE on one receipt. The backend
        preserves the coarse one only when it disagrees — showing it here is the point;
        a disagreement between two stored labels is itself a finding. */}
    {card.coarse_failure_class && (
      <Typography variant="caption" display="block" color="warning.main" sx={{ mt: 0.5 }}>
        stored classes disagree: weekly “{card.stored_failure_class}” vs coarse “
        {card.coarse_failure_class}”
      </Typography>
    )}

    {card.conflict && (
      <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
        <Typography variant="caption">{card.conflict}</Typography>
      </Alert>
    )}

    {/* Unrecognised vocabulary is a FINDING, not a bucket. When the scraper starts
        emitting new reason strings they must arrive visible rather than dissolving
        into the default bucket — that is exactly the signal a new fix creates. */}
    {card.unmatched_text.length > 0 && (
      <Alert severity="info" sx={{ mt: 1, py: 0 }}>
        <Typography variant="caption" component="div" fontWeight={700}>
          Unrecognised diagnostic text — the classifier map may need re-pinning against source.
        </Typography>
        {card.unmatched_text.map((text, i) => (
          <Typography key={i} variant="caption" component="div" sx={{ opacity: 0.85 }}>
            • {text}
          </Typography>
        ))}
      </Alert>
    )}
  </Box>
);

const FleetActionsModal: React.FC<FleetActionsModalProps> = ({
  actions,
  bucket,
  onClose,
  onSelectBucket,
}) => {
  if (!actions || !bucket) return null;

  const buckets = actions.bucket_order.filter((b) => (actions.counts[b] || 0) > 0);
  const rows = actions.properties.filter((p) => p.bucket === bucket);
  const tabIndex = Math.max(0, buckets.indexOf(bucket));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Fleet actions
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {actions.total} properties · generated {actions.generated_for}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tabIndex}
        onChange={(_e, index) => onSelectBucket(buckets[index])}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {buckets.map((b) => (
          <Tab
            key={b}
            label={`${b} (${actions.counts[b]})`}
            sx={{ fontWeight: 700 }}
          />
        ))}
      </Tabs>

      <DialogContent>
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          <Typography variant="caption">{BUCKET_HELP[bucket]}</Typography>
        </Alert>

        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No properties in this bucket.
          </Typography>
        ) : (
          rows.map((card, i) => (
            <React.Fragment key={card.property_id}>
              {i > 0 && <Divider />}
              <PropertyRow card={card} />
            </React.Fragment>
          ))
        )}

        <Divider sx={{ my: 2 }} />
        {/* The drift check, visible rather than buried in a comment: this classifier is a
            COPY of a function in another repository and cannot be imported. */}
        <Typography variant="caption" color="text.secondary" display="block">
          Classification mirrors {actions.classifier.source}. {actions.classifier.note}
        </Typography>
        {actions.classifier.pending_pin.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Not yet verified in source:{" "}
            {actions.classifier.pending_pin.map((p) => p.literal).join(", ")} — these surface as
            unrecognised text until someone reads them at a fetchable tree.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FleetActionsModal;
